import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, Card, CardHeader, ErrorState, Icon, PageHeader, PageLoader, useToast } from '../../../components/ui';
import { useCrumb } from '../../../components/layout/Breadcrumbs/Breadcrumbs';
import { KeyValues, ReasonDialog, StatusBadge, TwoColumn } from '../../../components/common/Parts/Parts';
import { useAction } from '../../../hooks/useAction';
import { useFetch } from '../../../hooks/useFetch';
import { labelOf } from '../../../utils/labels';
import { PAY_TYPES, PAYMENT_MODES } from '../../../constants/payments';
import { PAYMENT_STATUS } from '../../../constants/status';
import { formatDate, toISODate } from '../../../utils/date';
import { money } from '../../../utils/currency';
import { rupeesInWords } from '../../../lib/pricing';
import { periodText } from './PayslipList';
import { payrollService } from '../services/payrollService';
import type { ReactNode } from 'react';

type LineRow = [label: string, detail: ReactNode, amount: number];

function Lines({ title, rows, total, totalLabel }: { title: string; rows: (LineRow | false | null | 0)[]; total: number; totalLabel: string }) {
  return (
    <div>
      <p className="mb-2 text-[11px] font-medium uppercase tracking-wider text-ink-400">{title}</p>
      <table className="w-full text-sm">
        <tbody className="tabular divide-y divide-line">
          {(rows.filter(Boolean) as LineRow[]).map(([label, detail, amount]) => (
            <tr key={label}>
              <td className="py-2.5">
                <span className="font-medium text-navy-900">{label}</span>
                {detail && <span className="block text-xs text-ink-400">{detail}</span>}
              </td>
              <td className="py-2.5 text-right font-medium">{money(amount)}</td>
            </tr>
          ))}
          <tr className="border-t-2 border-navy-800">
            <td className="py-2.5 font-semibold">{totalLabel}</td>
            <td className="py-2.5 text-right font-semibold">{money(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function PayslipDetails() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { busy, run } = useAction(toast);
  const [cancelling, setCancelling] = useState(false);
  const { data: s, error, reload } = useFetch(() => payrollService.get(id), [id]);
  useCrumb(`/payroll/${id}`, s?.slipNo);

  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!s) return <PageLoader />;

  const active = s.status === 'active';
  const cancel = async (reason) => {
    const res = await run('cancel', () => payrollService.cancel(id, reason), 'Salary slip cancelled');
    setCancelling(false);
    if (res) reload();
  };
  const deductions = s.otherDeductions + s.advanceDeduction;
  const daily = s.payType === 'daily';

  return (
    <>
      <PageHeader
        title={s.slipNo}
        badge={active ? <StatusBadge map={PAYMENT_STATUS} value={s.paymentStatus} /> : <Badge tone="red" dot>Cancelled</Badge>}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-5 gap-y-1">
            <span className="inline-flex items-center gap-1.5"><Icon name="user" size={15} />{s.staff?.name}</span>
            <span className="inline-flex items-center gap-1.5"><Icon name="calendar" size={15} />{periodText(s)}</span>
            <span className="inline-flex items-center gap-1.5"><Icon name="wallet" size={15} />{labelOf(PAY_TYPES, s.payType)}</span>
          </span>
        }
        actions={
          <>
            {active && s.paidAmount === 0 && <Button variant="dangerText" onClick={() => setCancelling(true)}>Cancel slip</Button>}
            <Button variant="outline" leftIcon="calendar" onClick={() => navigate(`/attendance?view=sheet&month=${toISODate(new Date(s.periodTo)).slice(0, 7)}`)}>Attendance</Button>
            <Button variant="outline" leftIcon="download" loading={busy === 'pdf'} onClick={() => run('pdf', () => payrollService.openPdf(id))}>Print / PDF</Button>
            {active && s.balanceAmount > 0 && <Button leftIcon="rupee" onClick={() => navigate(`/transactions/new?payslipId=${id}`)}>Pay salary</Button>}
          </>
        }
      />
      {!active && <p className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">Cancelled: {s.cancelReason}. Its days can be marked and paid again.</p>}

      <TwoColumn
        main={
          <Card className="p-5 sm:p-8">
            <div className="flex flex-wrap items-start justify-between gap-4 border-b border-line pb-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-brand-600">Salary slip</p>
                <p className="text-xl font-semibold text-navy-900">{s.staff?.name}</p>
                <p className="text-[13px] text-ink-500">{[s.staff?.phone, s.staff?.role === 'office' ? 'Office staff' : 'Worker'].filter(Boolean).join(' · ')}</p>
              </div>
              <div className="text-right text-[13px]">
                <p className="text-ink-500">Period</p>
                <p className="font-medium text-navy-900">{periodText(s)}</p>
                <p className="mt-1 text-ink-500">{daily ? `${money(s.rate)} per day` : `${money(s.rate)} per month`}</p>
              </div>
            </div>

            <div className="mt-5 grid gap-3 rounded-lg bg-navy-50/60 p-4 text-center text-sm sm:grid-cols-5">
              {[
                ['Present', s.presentDays],
                ['Half day', s.halfDays],
                ['Absent', s.absentDays],
                ['Off', s.holidayDays],
                ['Days paid', s.payableDays],
              ].map(([l, v]) => (
                <div key={l}>
                  <p className="tabular text-lg font-semibold text-navy-900">{v}</p>
                  <p className="text-xs text-ink-500">{l}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 grid gap-6 lg:grid-cols-2">
              <Lines
                title="Earnings"
                totalLabel="Total earnings"
                total={s.grossAmount}
                rows={[
                  ['Basic pay', daily ? `${s.payableDays} days × ${money(s.rate)}` : `${s.payableDays} of ${s.days} days`, s.basicAmount],
                  s.overtimeAmount > 0 && ['Overtime', `${s.overtimeHours} hours × ${money(s.overtimeRate)}`, s.overtimeAmount],
                  s.bonus > 0 && ['Bonus / incentive', null, s.bonus],
                ]}
              />
              <Lines
                title="Deductions"
                totalLabel="Total deductions"
                total={deductions}
                rows={[
                  ['Advance recovered', null, s.advanceDeduction],
                  s.otherDeductions > 0 && ['Other deductions', null, s.otherDeductions],
                ]}
              />
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-lg bg-brand-50 px-5 py-4">
              <div>
                <p className="text-sm font-semibold text-navy-900">Net pay</p>
                <p className="text-xs text-ink-500">{rupeesInWords(s.netAmount)}</p>
              </div>
              <p className="tabular text-2xl font-semibold text-brand-700">{money(s.netAmount)}</p>
            </div>
            {s.note && <p className="mt-4 text-[13px] text-ink-600">Note: {s.note}</p>}
          </Card>
        }
        aside={
          <>
            <Card>
              <CardHeader title="Payment" />
              <div className="p-5 pt-4">
                <KeyValues
                  rows={[
                    ['Net pay', money(s.netAmount), { strong: true }],
                    ['Paid', money(s.paidAmount), { tone: 'text-emerald-700' }],
                    ['Due', money(s.balanceAmount), { strong: true, tone: s.balanceAmount > 0 ? 'text-red-600' : undefined }],
                  ]}
                />
              </div>
              {(s.payments?.length ?? 0) > 0 && (
                <ul className="divide-y divide-line border-t border-line">
                  {s.payments?.map((t) => (
                    <li key={t._id}>
                      <Link to={`/transactions/${t._id}`} className="flex justify-between gap-3 px-5 py-2.5 text-sm hover:bg-navy-50/60">
                        <span>
                          {formatDate(t.date)} · {labelOf(PAYMENT_MODES, t.mode)}
                          {t.isReversed && <span className="text-xs text-red-600"> · reversed</span>}
                        </span>
                        <span className={`tabular font-semibold ${t.isReversed ? 'line-through opacity-60' : ''}`}>{money(t.amount)}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <Card>
              <CardHeader title="Staff" action={<Link to={`/team/${s.userId}/edit`} className="text-sm font-medium text-brand-600 hover:underline">Open</Link>} />
              <div className="p-5 pt-4">
                <KeyValues rows={[['Name', s.staff?.name], ['Phone', s.staff?.phone || '-'], ['Advances', <Link to="/staff-advances" className="text-brand-600 hover:underline">See balance</Link>]]} />
              </div>
            </Card>
          </>
        }
      />
      <ReasonDialog
        open={cancelling}
        onClose={() => setCancelling(false)}
        onConfirm={cancel}
        loading={busy === 'cancel'}
        title="Cancel this salary slip?"
        message="Any advance it recovered becomes pending again, and its days can be paid on a new slip."
        confirmLabel="Cancel slip"
      />
    </>
  );
}
